import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';

const formatTimestamp = (value) => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const formatDetails = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const getStatusColor = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (['success', 'succeeded', 'ok', 'completed', 'done'].includes(normalized)) {
    return 'success';
  }
  if (['failed', 'failure', 'error'].includes(normalized)) {
    return 'error';
  }
  if (['skipped', 'skip'].includes(normalized)) {
    return 'warning';
  }
  return 'default';
};

const PluginLogsPanel = ({
  logs = [],
  loading = false,
  error = '',
  pluginType = '',
  onRefresh,
  t,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ sm: 'center' }}
      justifyContent="space-between"
      sx={{ mb: 1 }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('global_setting.plugins_logs_title', 'Backend activity')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'global_setting.plugins_logs_description',
            'Execution history recorded by the backend for this plugin.'
          )}
          {pluginType ? ` · ${pluginType}` : ''}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        startIcon={loading ? <CircularProgress size={15} /> : <RefreshIcon />}
        onClick={onRefresh}
        disabled={loading}
      >
        {t('global_setting.plugins_logs_refresh', 'Refresh')}
      </Button>
    </Stack>

    {error ? (
      <Alert severity="error" sx={{ mb: 1 }}>
        {error}
      </Alert>
    ) : null}

    {loading && !logs.length ? (
      <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    ) : null}

    {!loading && !logs.length ? (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <HistoryIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t('global_setting.plugins_logs_empty', 'No backend activity has been recorded for this plugin yet.')}
        </Typography>
      </Paper>
    ) : null}

    {logs.length ? (
      <Stack spacing={1} sx={{ overflowY: 'auto', pr: 0.5, minHeight: 0 }}>
        {logs.map((log, index) => {
          const details = formatDetails(log?.details);
          const status = String(log?.status || t('global_setting.plugins_logs_unknown_status', 'Unknown'));
          return (
            <Paper
              key={log?.id || `${log?.timestamp || 'log'}-${index}`}
              variant="outlined"
              sx={{ p: 1.25 }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <Chip label={status} size="small" color={getStatusColor(log?.status)} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatTimestamp(log?.timestamp) || t('global_setting.plugins_logs_unknown_time', 'Unknown time')}
                </Typography>
                {log?.durationMs !== null && log?.durationMs !== undefined ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('global_setting.plugins_logs_duration', '{{duration}} ms', { duration: log.durationMs })}
                  </Typography>
                ) : null}
                {log?.executionContext ? (
                  <Typography variant="caption" color="text.secondary">
                    {log.executionContext}
                  </Typography>
                ) : null}
                {log?.caseId !== null && log?.caseId !== undefined ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('global_setting.plugins_logs_case', 'Project {{id}}', { id: log.caseId })}
                  </Typography>
                ) : null}
              </Stack>
              {details ? (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    component="pre"
                    variant="caption"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      color: 'text.secondary',
                      fontFamily: 'monospace',
                    }}
                  >
                    {details}
                  </Typography>
                </>
              ) : null}
            </Paper>
          );
        })}
      </Stack>
    ) : null}
  </Box>
);

export default PluginLogsPanel;
